import { css } from "@emotion/react";
import { kitBackground, kitShadow, kitText } from "../../styles";

const chat = {
  base: css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;
    --chat-padding: 0px;
    position: relative;
    .kit-ChatMessages {
      padding-top: var(--chat-messages-padding-top, 0px);
      padding-bottom: var(--chat-messages-padding-bottom, 0px);
    }
  `,
  padded: css`
    --chat-padding: var(--spacing-24);
  `,
  maverickTheme: css`
    --chat-background: var(--color-background-secondary);
    background-color: var(--chat-background);
  `
};

const input = {
  base: css`
    padding: var(--spacing-12) var(--chat-padding) var(--chat-padding);
    bottom: 0;
    position: sticky;
  `,
  wrapper: css`
    ${kitShadow.small}
    display: flex;
    flex-direction: column;
    border-radius: var(--spacing-8);
    border: 1px solid var(--color-border-primary);
    background-color: var(--color-background-primary);
    padding: var(--spacing-8);
    width: 100%;
    &:focus-within {
      border-color: var(--color-splash-primary);
    }
  `,
  inputArea: css`
    display: flex;
    gap: var(--spacing-8);
    flex-grow: 1;
    background-color: var(--color-background-primary);
  `,
  textarea: css`
    ${kitText.variant.body}
    width: 100%;
    max-height: 200px;
    resize: none;
    border-color: var(--color-border-secondary);
    background-color: var(--color-background-primary);
    border: none;
    outline: none;
    padding: var(--spacing-4);
  `,
  submitButton: css`
    padding: 0;
    width: var(--spacing-24);
    height: var(--spacing-24);
  `,
  abortButton: css`
    border-radius: 9999px;
  `,
  disabled: css``,
  uploader: css`
    display: flex;
    gap: var(--spacing-4);
    &:has(div.subKit-UploaderFileListItem) {
      margin-bottom: var(--spacing-8);
    }
  `,
  topActions: css`
    margin-bottom: var(--spacing-4);
  `
};

const messages = {
  base: css`
    display: flex;
    flex-direction: column;
    padding: var(--chat-padding);
    padding-bottom: var(--chat-padding-bottom, 0);
    height: 100%;
    flex-grow: 1;
    overflow-y: auto;
  `,
  noMessages: css`
    ${kitText.variant.heading1}
    padding-top: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: var(--spacing-48) 0;
  `
};

const message = {
  base: css`
    ${kitText.variant.body}
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    flex-direction: row;
    flex-wrap: nowrap;
    margin-left: auto;
    margin-right: 0;
    gap: var(--spacing-8);
    width: 75%;
    min-width: 180px;
  `,
  receivedBase: css`
    min-width: 180px;
    width: 100%;
    max-width: fit-content;
    word-break: break-word;
    flex-direction: row-reverse;
    margin-left: 0;
  `,
  contentWrapper: css`
    display: flex;
    flex-direction: column;
    margin-top: var(--spacing-4);
    margin-bottom: var(--spacing-4);
    align-items: flex-start;
    > *:last-child {
      margin-bottom: 0 !important;
    }
  `,
  content: css`
    border-radius: var(--spacing-8);
    padding: var(--spacing-8) 0;
  `,
  sentContent: css`
    background: var(--color-splash-primary);
    color: #fff;
    padding: var(--spacing-12);
    margin-left: auto;
  `,

  bottomActions: css`
    --icon-color: var(--color-icon-secondary);
    width: 100%;
    margin-top: var(--spacing-4);
  `,
  icon: css`
    margin: var(--spacing-8) 0 0 0;
  `,
  topAttachments: css`
    margin-bottom: var(--spacing-8);
  `,
  bottomAttachments: css`
    margin-top: var(--spacing-8);
  `,
  title: css`
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
  `
};

const cue = {
  wrapper: css`
    position: relative;
    .kit-ChatCues[aria-expanded="true"] & {
      opacity: 1;
      transition: opacity 0.15s ease-in;
      &.not-visible {
        opacity: 0.3;
      }
    }
  `,
  base: css`
    ${kitText.variant.body}
    background: var(--color-background-primary);
    border: 1px solid var(--color-border-secondary);
    padding: var(--spacing-12);
    border-radius: var(--spacing-8);
    width: fit-content;
    overflow-wrap: break-word;
    text-wrap: pretty;
    height: auto;
    will-change: opacity;

    &:hover {
      border-color: var(--border-color-secondary);
    }

    &:active,
    &:focus {
      background: var(--color-splash-primary);
      color: #fff;
      border-color: var(--color-splash-primary);
    }
  `,
  intersectionTarget: css`
    position: absolute;
    top: 0px;
    /* Extend the intersection area 30px below the cue so visibility/scroll
       detection can trigger slightly before/after the cue fully enters the
       viewport. */
    bottom: -30px;
    width: 100%;
    z-index: -1;
    pointer-events: none;
  `,
  disabled: css`
    color: var(--color-secondary);
    background-color: transparent;
    border-color: transparent;
    pointer-events: none;
  `
};

const cues = {
  base: css`
    --chat-cues-padding: var(--spacing-16);
    background: var(--chat-background, var(--color-background-primary));
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding-top: var(--chat-cues-padding);
    gap: var(--spacing-8);
    opacity: 0;
    animation-name: chat-cues-reveal;
    animation-duration: 0.75s;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;

    @keyframes chat-cues-reveal {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  `,
  optionsWrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);
    margin-top: auto;
  `,
  optionsWrapperExpanded: css`
    overflow-y: auto;
    max-height: 33vh;
  `,
  expandTarget: css`
    ${kitText.variant.body}
    ${kitText.color.secondary}
    padding: var(--spacing-4) 0 0;
    width: 100%;
    position: sticky;
    bottom: 0;
    ${kitBackground.blurred.secondary}
  `
};

const header = {
  base: css`
    position: sticky;
    top: 0;
    z-index: 1;
    padding: var(--spacing-24);
    display: flex;
    align-items: center;
    gap: var(--spacing-12);
    position: absolute;
    width: 100%;
    ${kitBackground.blurred.secondary}
  `,
  title: css`
    ${kitText.variant.heading4}
    ${kitText.style.bold}
    margin-right: auto;
  `
};

export const styles = { chat, input, messages, message, cue, cues, header };
